import React, { useEffect } from 'react';
import { motion, Variants } from 'framer-motion';

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    // Smooth scroll behavior for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href')?.substring(1);
        if (!targetId) return;

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop - 100, // Offset to account for sticky header
            behavior: 'smooth'
          });

          // Update URL hash without jumping
          window.history.pushState(null, '', `#${targetId}`);
        }
      });
    });
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{
      background: 'linear-gradient(135deg, #141019 0%, #1A1625 50%, #1F1A27 100%)',
    }}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/10 blur-[170px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-500/10 blur-[170px] rounded-full"></div>

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(to right, #ffffff 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Decorative circles */}
      <div className="absolute right-20 top-20 opacity-30">
        <div className="w-4 h-4 rounded-full border-2 border-[#F670C7]"></div>
        <div className="w-3 h-3 rounded-full bg-[#F670C7]/30 absolute -bottom-4 -left-2"></div>
      </div>

      <div className="absolute left-[10%] bottom-[20%] opacity-20">
        <div className="w-32 h-32 rounded-full border border-[#F670C7]"></div>
        <div className="w-16 h-16 rounded-full border border-white/30 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block mb-4 px-4 py-1 bg-white/10 backdrop-blur-md rounded-full text-[#F670C7] text-sm font-medium"
            >
              Legal Information
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight"
            >
              Privacy Policy
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/70 text-lg max-w-2xl mx-auto"
            >
              How we collect, use, and protect your information on Developer Marketplace.
            </motion.p>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="prose prose-invert prose-headings:font-medium prose-headings:mb-4 prose-p:mb-4 prose-p:text-white/80 prose-li:text-white/80 prose-li:mb-2 max-w-none backdrop-blur-sm bg-gradient-to-r from-[#1F1A27]/90 to-[#2A1E44]/90 p-8 md:p-10 rounded-2xl shadow-xl border border-white/10"
          >
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
              <p className="text-white text-lg m-0">
                Last updated: June 15, 2023
              </p>

              <div className="flex items-center space-x-3">
                <button onClick={() => window.print()} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Print</span>
                </button>

                <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Copy Link</span>
                </button>
              </div>
            </div>

            <div className="md:grid md:grid-cols-[250px_1fr] gap-8">
              {/* Table of Contents (Desktop) */}
              <div className="hidden md:block">
                <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pr-4 pb-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                  <h4 className="text-sm uppercase tracking-wider text-white/50 mb-4 font-medium">Contents</h4>
                  <nav className="space-y-2">
                    <a href="#introduction" className="block text-white/70 hover:text-[#F670C7] transition-colors">Introduction</a>
                    <a href="#information-collection" className="block text-white/70 hover:text-[#F670C7] transition-colors">Information Collection</a>
                    <a href="#data-security" className="block text-white/70 hover:text-[#F670C7] transition-colors">Data Storage & Security</a>
                    <a href="#cookies" className="block text-white/70 hover:text-[#F670C7] transition-colors">Cookies & Tracking</a>
                    <a href="#service-providers" className="block text-white/70 hover:text-[#F670C7] transition-colors">Service Providers</a>
                    <a href="#disclosure" className="block text-white/70 hover:text-[#F670C7] transition-colors">Disclosure for Law Enforcement</a>
                    <a href="#international" className="block text-white/70 hover:text-[#F670C7] transition-colors">International Data Transfers</a>
                    <a href="#childrens" className="block text-white/70 hover:text-[#F670C7] transition-colors">Children's Privacy</a>
                    <a href="#changes" className="block text-white/70 hover:text-[#F670C7] transition-colors">Changes to This Policy</a>
                    <a href="#contact" className="block text-white/70 hover:text-[#F670C7] transition-colors">Contact Us</a>
                  </nav>

                  <div className="mt-12 p-5 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h5 className="font-medium text-white m-0 text-base">Privacy Commitment</h5>
                    </div>
                    <p className="text-sm text-white/70 m-0">We're committed to protecting your personal information and being transparent about how we use it.</p>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div>
                <section id="introduction">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Introduction</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Developer Marketplace ("we," "us," or "our") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, and disclose your information when you use our online marketplace for developers ("Service").
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    By using our Service, you agree to the collection and use of information in accordance with this policy. We will not use or share your information with anyone except as described in this Privacy Policy.
                  </p>
                </section>

                <section id="information-collection">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Information Collection and Use</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Email address</li>
                    <li className="text-white/90 leading-relaxed">First name and last name</li>
                    <li className="text-white/90 leading-relaxed">Phone number</li>
                    <li className="text-white/90 leading-relaxed">Address, State, Province, ZIP/Postal code, City</li>
                    <li className="text-white/90 leading-relaxed">Payment information</li>
                    <li className="text-white/90 leading-relaxed">Usage data</li>
                  </ul>
                </section>

                <section id="data-security">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Data Storage and Security</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    The security of your data is important to us. We strive to use commercially acceptable means to protect your personal information, but we cannot guarantee its absolute security. We implement a variety of security measures to maintain the safety of your personal information when you enter, submit, or access your personal information.
                  </p>

                  <div className="bg-white/5 p-6 rounded-xl my-8 border border-white/10">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Security Measures We Implement
                    </h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li className="text-white/90">Encryption of transmitted data using SSL technology</li>
                      <li className="text-white/90">Regular security assessments and penetration testing</li>
                      <li className="text-white/90">Secure data storage with controlled access</li>
                      <li className="text-white/90">Employee security training and access controls</li>
                      <li className="text-white/90">Prompt security updates and patches</li>
                    </ul>
                  </div>
                </section>

                <section id="cookies">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Cookies and Tracking</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
                  </p>
                </section>

                <section id="service-providers">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Service Providers</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We may employ third-party companies and individuals to facilitate our Service, provide the Service on our behalf, perform Service-related services, or assist us in analyzing how our Service is used. These third parties have access to your personal information only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                  </p>
                </section>

                <section id="disclosure">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Disclosure for Law Enforcement</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Under certain circumstances, we may be required to disclose your personal information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).
                  </p>
                </section>

                <section id="international">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">International Data Transfers</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Your information, including personal information, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including personal information, to the United States and process it there.
                  </p>
                </section>

                <section id="childrens">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Children's Privacy</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.
                  </p>
                </section>

                <section id="changes">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Changes to This Privacy Policy</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                <section id="contact">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Contact Us</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    If you have any questions about this Privacy Policy, please contact us:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">By email: <span className="text-[#F670C7]">privacy@developermarketplace.com</span></li>
                    <li className="text-white/90 leading-relaxed">By visiting the <span className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">contact page</span> on our website</li>
                  </ul>
                </section>

                <div className="mt-12 p-6 bg-gradient-to-r from-[#7F56D9]/20 to-[#F670C7]/20 rounded-xl border border-white/10 backdrop-blur-sm">
                  <div className="flex items-center mb-4">
                    <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white">Need More Information?</h3>
                  </div>
                  <p className="text-white/90 mb-4">
                    If you have additional questions about our privacy practices or need assistance with your data, our privacy team is here to help.
                  </p>
                  <a href="/privacy-request" className="inline-flex items-center text-[#F670C7] hover:text-[#F670C7]/80 font-medium">
                    Submit a privacy request
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
